<template>
  <Wrap v-show="!modelValue">
    <div
      id="sights"
      class="sights"
    >
      <div class="list">
        <template v-if="visibleSights.length">
          <SightCard
            v-for="sight in visibleSights"
            :key="sight.id"
            :sight="sight"
            @mouseenter="hover(sight, true)"
            @mouseleave="hover(sight, false)"
          />
        </template>
        <template v-else>
          <SightCardSkeleton
            v-for="el in 9"
            :key="`sk-${el}`"
          />
        </template>
      </div>
      <div
        v-if="showLoadMore"
        class="show-more-wrap"
      >
        <UiButton
          :variant="UiButtonVariant.OUTLINE"
          :color-mode="UiButtonColorMode.NEUTRAL"
          :is-full-width="isMobile"
          text="Показать еще"
          @clicked="showMore"
        />
      </div>
    </div>
  </Wrap>

  <UiModal
    :is-shown="sightsStore.isOpenDetailsModal"
    modal-class="modal-sight"
    size="m"
    is-header-fixed
    :title="sightsStore.activeSight.name"
    :teleport-target="modalTarget || undefined"
    :is-content-no-padding="true"
    :is-footer-hidden="true"
    :is-close-button-filled="true"
    @close="sightsStore.closeModalDetails"
  >
    <template #content>
      <ModalContainer
        v-if="sightsStore.isOpenDetailsModal"
        :city="city"
        :sight="sightsStore.activeSight"
      />
    </template>
  </UiModal>

  <Transition>
    <div
      v-if="modelValue"
      class="full-size-modal-map"
    >
      <ModalMap
        :sights="sights"
        :is-view-more="isViewMore"
        @is-view-sights="updateSights"
        @hover-update="hoverUpdate"
        @close="closeMapModal"
      />
    </div>
  </Transition>
</template>

<script setup lang="ts">
  import { UiButton, UiButtonVariant, UiButtonColorMode } from '@experience/core-button-ui';
  import { UiModal } from '@experience/core-modal-ui';
  import { computed, ref, onMounted, type useShadowRoot, inject } from 'vue';
  import { useRoute } from 'vue-router';

  import { trackEvent } from '@/services/analytics';
  import { getSights } from '@/shared/api/generated/api';
  import Wrap from '@/shared/ContentWrap.vue';
  import { type City } from '@/shared/models/faq';
  import { type CityTagsAdvanced } from '@/shared/models/types';
  import { useSightsStore } from '@/shared/store/sights';
  import ModalContainer from '@/shared/ui/ModalContainer.vue';
  import ModalMap from '@/shared/ui/ModalMap.vue';
  import SightCard from '@/shared/ui/SightCard.vue';
  import SightCardSkeleton from '@/shared/ui/SightCardSkeleton.vue';
  import { useSightAnalyticsParams } from '@/shared/utils/analytics';
  import { useScreenSize } from '@/shared/utils/breakpoints';

  const sightsStore = useSightsStore();
  const analyticsParams = useSightAnalyticsParams();

  const route = useRoute();

  const emit = defineEmits(['update:modelValue']);

  defineProps<{
    modelValue: boolean;
    city: City;
  }>();

  const modalTarget = ref<HTMLElement | null>(null);

  const rootNode = inject<ReturnType<typeof useShadowRoot>>('shadowRoot', null);

  if (rootNode) {
    modalTarget.value = rootNode.querySelector('.modal-target');
  } else {
    modalTarget.value = document.querySelector('.modal-target');
  }

  const { isMobile } = useScreenSize();

  const slug = route.params.slug as string;

  const PAGE_SIZE = 30;
  const sights = ref<CityTagsAdvanced[]>([]);
  const visibleCount = ref(PAGE_SIZE);
  const visibleSights = computed(() => sights.value.slice(0, visibleCount.value));
  const showLoadMore = computed(() => visibleCount.value < sights.value.length);
  const isViewMore = ref<boolean>(false);

  const fetchSight = async () => {
    if (!slug) {
      return;
    }

    try {
      sightsStore.sightsIsLoaded = true;
      const { data } = await getSights({
        query: {
          city_slug: slug,
          page_size: 10000,
          sorting: '-excursions_count',
        },
      });

      if (!data?.results) {
        return;
      }

      sights.value = data.results;
    } catch {
      sightsStore.sightsIsLoaded = false;
    }
  };

  const showMore = () => {
    void trackEvent('attractions:sights-snippet:show-more-button.click', analyticsParams.value);
    visibleCount.value += PAGE_SIZE;
  };

  const hover = (sight: CityTagsAdvanced, isHovered: boolean) => {
    const hoverEl = sights.value.find((el) => el.id === sight.id);

    if (hoverEl) {
      hoverEl.isHovered = isHovered;
    }
  };

  const checkViewMoreCard = () => {
    isViewMore.value = !!sights.value.find((el) => !el.isView);
  };

  const updateSights = (list: CityTagsAdvanced[]) => {
    sights.value = list;
    checkViewMoreCard();
  };

  onMounted(() => {
    void fetchSight();
  });

  const hoverUpdate = (id: number) => {
    sights.value.forEach((el) => {
      el.isHovered = el.id === id;
    });
  };

  const closeMapModal = () => {
    emit('update:modelValue', false);
  };
</script>

<style lang="scss" scoped>
  @use '@experience/design-tokens/typography' as legacy-typography;
  @use '@experience/shared/dist/assets/adaptivity' as legacy-adaptivity;

  .sights {
    position: relative;
    height: 100%;
    margin-top: var(--alias-space-48);

    @include legacy-adaptivity.tablet {
      margin-top: var(--alias-space-56);
    }
  }

  .list {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 24px;
    width: 100%;

    @include legacy-adaptivity.tablet {
      grid-template-columns: repeat(2, 1fr);
    }

    @include legacy-adaptivity.mobile {
      grid-template-columns: 1fr;
      gap: var(--alias-space-32);
    }
  }

  .map {
    position: sticky;
    top: 24px;
    bottom: 24px;
    grid-area: map;
    width: 100%;
    height: calc(100vh - 48px);
    overflow: hidden;
    border-radius: var(--alias-radius-16, 16px);

    @include legacy-adaptivity.tablet {
      display: none;
    }
  }

  .btn-skeleton {
    height: 48px;
    border-radius: var(--alias-radius-8);
  }

  .view-more-card {
    width: 100%;
    padding: var(--alias-space-40);

    span {
      display: block;
      max-width: 288px;
      text-align: center;

      @include legacy-typography.base-400-300-regular;
    }

    .btn-map-more {
      display: block;
      margin: var(--alias-space-16) auto 0;
    }
  }

  .map-skeleton {
    width: 100%;
    height: 100vh;
  }
</style>

<style lang="scss">
  .modal-sight .ui-modal-header {
    z-index: 12 !important;
  }

  .modal-sight .ui-modal-body {
    overflow: visible !important;
  }

  .modal-sight-map {
    max-height: 100% !important;
  }

  .full-size-modal-map {
    position: fixed;
    inset: 0;
    z-index: 1001;
    width: 100%;
    height: 100%;
    background: var(--alias-color-background-default);
  }

  .v-enter-active,
  .v-leave-active {
    transition: opacity 0.2s ease;
  }

  .v-enter-from,
  .v-leave-to {
    opacity: 0;
  }

  .show-more-wrap {
    display: flex;
    justify-content: center;
    margin-top: 24px;

    @include adaptivity.tablet {
      margin-top: 32px;
    }

    @include adaptivity.mobile {
      margin-top: 24px;
    }
  }
</style>
